import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatMoney } from "utilities/formatMoney";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import { useDispatch, useSelector } from "react-redux";
import CurrencyToggle from "component/currencyToggle";
import {
    resetTransfer,
    setAmount,
    setCurrency,
    setProvider,
} from "../../../../redux/slices/tradevuTransferSlice";
import currencyPairCheck from "utilities/currencyPairCheck";
import { ArrowRight2, Timer1 } from "iconsax-reactjs";
import { resetBankTransfer } from "../../../../redux/slices/bankTransferSlice";
import { resetUsdTransfer } from "../../../../redux/slices/usdTransferSlice";
import { resetCryptoTransfer } from "../../../../redux/slices/cryptoTransferSlice";
import { resetCryptoFiatTransfer } from "../../../../redux/slices/cryptoFiatTransferSlice";
import { format, InputNumberFormat, unformat } from "@react-input/number-format";
import SelectBankProviderModal from "component/wallet/SelectBankProviderModal";

const SameCurrencyTradevuUser = () => {
    const [amount, setAmountInput] = useState("");
    const [provider, setProviderState] = useState(null);
    const [showProviderModal, setShowProviderModal] = useState(false);
    const { business, currency, wallets } = useSelector((state) => state);
    const dispatch = useDispatch();
    const location = useLocation();
    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;
    const navigate = useNavigate();
    const minAmount = 10;
    const LOCALE = "en";

    useEffect(() => {
        sessionStorage.setItem("previousTransactionRoute", location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
    }, [dispatch]);

    useEffect(() => {
        setProviderState(null);
    }, [currency]);

    const currentWallet = wallets?.find(
        (wallet) =>
            String(wallet.currency || "").toUpperCase() === String(currency || "").toUpperCase()
    );
    const providers = useMemo(
        () => (Array.isArray(currentWallet?.providers) ? currentWallet.providers : []),
        [currentWallet]
    );
    const isKES = String(currency || "").toUpperCase() === "KES";
    const hasMultipleProviders = providers.length > 1 && !isKES;

    const selectedProviderBalance = useMemo(() => {
        if (!hasMultipleProviders || !provider) return null;
        const value = Number(provider?.balance);
        return Number.isFinite(value) ? value : null;
    }, [hasMultipleProviders, provider]);

    const balance =
        selectedProviderBalance !== null ? selectedProviderBalance : currentWallet?.balance || 0;
    const numericAmount = Number.parseFloat(amount || "0") || 0;
    const isAmountValid = numericAmount >= minAmount;
    const isOverBalance = numericAmount > balance;
    const isBelowMinimum = numericAmount > 0 && numericAmount < minAmount;
    const needsProvider = hasMultipleProviders && !provider;
    const isButtonDisabled = !isAmountValid || isOverBalance || needsProvider;

    const formatted = amount ? format(amount, { locales: LOCALE, maximumFractionDigits: 2 }) : "";
    const amountError = isOverBalance
        ? `Insufficient balance. Available: ${currencyPairCheck(currency)}${formatMoney(balance)}`
        : isBelowMinimum
          ? `Minimum transfer amount is ${currencyPairCheck(currency)}${formatMoney(minAmount)}`
          : "";
    const continueBlockReason = needsProvider ? "Select a bank account to continue" : amountError;

    const handleAmountChange = (event) => {
        const raw = unformat(event.target.value || "", LOCALE);
        setAmountInput(raw);
    };

    const handleContinue = () => {
        dispatch(setAmount(amount));
        dispatch(setCurrency(currency));
        dispatch(setProvider(provider));
        navigate("/payment/send-money/same-currency/tradevu-user/recipient-info");
    };

    return (
        <OnboardingLayout title="Send money" showSkip={false} allowContentOverflow>
            <div className="max-w-2xl mx-auto mt-10 space-y-10 px-2 sm:px-4 md:px-3 pb-32 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-xl sm:text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white rounded-xl p-3 sm:p-6 w-full">
                    <div className="border-b pb-4 mb-4">
                        <div className="flex items-center justify-between gap-4 mb-2">
                            <span className="text-[14px] font-[450] text-[#383B56]">You send</span>
                            <CurrencyToggle
                                className="text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 !right-0 !top-[109%] !min-w-0 !w-[calc(100vw-40px)] !max-w-[320px] sm:!w-[320px]"
                                parentClassName="shrink-0"
                                fetchWallet
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel
                            />
                        </div>

                        <div
                            className={`flex items-center min-w-0 text-3xl sm:text-5xl md:text-7xl font-normal leading-none ${
                                isOverBalance ? "text-red-600" : ""
                            }`}
                        >
                            <span className="mr-2 shrink-0 font-thin text-[#475467]">
                                {currencyPairCheck(currency)}
                            </span>
                            <InputNumberFormat
                                value={formatted}
                                onChange={handleAmountChange}
                                locales={LOCALE}
                                maximumFractionDigits={2}
                                type="text"
                                inputMode="decimal"
                                className={`!shadow-none min-w-0 flex-1 bg-transparent font-thin outline-none caret-[#6F00FF] placeholder:text-[#C4C6DE] ${
                                    isOverBalance ? "border-b border-red-500" : ""
                                }`}
                                placeholder="0"
                                aria-invalid={Boolean(amountError)}
                                aria-describedby={amountError ? "tradevu-amount-error" : undefined}
                            />
                        </div>

                        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 text-sm">
                                {amountError ? (
                                    <p
                                        id="tradevu-amount-error"
                                        role="alert"
                                        className="text-red-600"
                                    >
                                        {amountError}
                                    </p>
                                ) : (
                                    <p className="text-[#6F739B]">
                                        Minimum amount is {currencyPairCheck(currency)}
                                        {formatMoney(minAmount)}
                                    </p>
                                )}
                            </div>
                            <p className="shrink-0 text-sm text-[#6B7280] sm:text-right">
                                Balance: {currencyPairCheck(currency)}
                                {formatMoney(balance)}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3 text-[16px] text-[#161827]">
                        {hasMultipleProviders && (
                            <div className="flex flex-col gap-2 border-b border-[#E5E7EB] pb-3 sm:flex-row sm:items-center sm:justify-between">
                                <p className="min-w-0 break-words font-[450]">
                                    {provider ? (
                                        <>
                                            Sending via {provider.bankName || provider.bank || "—"}{" "}
                                            ({provider.accountNumber || "—"})
                                        </>
                                    ) : (
                                        "No bank account selected"
                                    )}
                                </p>

                                <button
                                    type="button"
                                    onClick={() => setShowProviderModal(true)}
                                    className="inline-flex shrink-0 items-center gap-1 self-start font-[450] text-[#7A27FF] sm:self-auto"
                                    aria-haspopup="dialog"
                                >
                                    {provider ? "Change bank" : "Select bank"}
                                    <ArrowRight2 size={18} />
                                </button>
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-4 font-normal">
                            <span>Transfer fee</span>
                            <span className="text-[#667085]">{currencyPairCheck(currency)}0</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 font-normal">
                            <span>Total to deduct</span>
                            <span className="text-right">
                                {currencyPairCheck(currency)}
                                {formatMoney(numericAmount)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="hidden md:flex justify-between items-center border-t border-[#E5E7EB] pt-4 mt-6">
                    <div className="flex items-center gap-2 text-sm text-[#161827]">
                        <Timer1 size="22" color="#6F739B" /> Sent instantly
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-[#111827] text-[16px] font-[450]"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={handleContinue}
                            disabled={isButtonDisabled}
                            title={
                                continueBlockReason ||
                                (!numericAmount ? "Enter a valid amount" : "")
                            }
                            className={`rounded-xl px-6 py-2 font-semibold text-white transition ${
                                !isButtonDisabled
                                    ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                    : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                            }`}
                        >
                            Continue
                        </button>
                    </div>
                </div>

                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-3 sm:px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">Sent instantly</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="text-[#111827] text-[16px] font-[450] w-[40%]"
                            >
                                Back
                            </button>

                            <button
                                type="button"
                                onClick={handleContinue}
                                disabled={isButtonDisabled}
                                title={
                                    continueBlockReason ||
                                    (!numericAmount ? "Enter a valid amount" : "")
                                }
                                className={`h-12 rounded-xl !px-5 font-semibold text-white transition w-[48%] ${
                                    !isButtonDisabled
                                        ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                        : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                                }`}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectBankProviderModal
                isOpen={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onSelect={(p) => setProviderState(p)}
                selectedId={provider?._id || provider?.accountNumber}
                providers={providers}
                currency={currency}
            />
        </OnboardingLayout>
    );
};

export default SameCurrencyTradevuUser;
